import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useUscgMapContext } from './UscgMapContext';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { LayerMetadata } from './LayerDataRows';
import { useCreateDraftInternalLayerVersionMutation } from 'hooks/internalLayerVersion/useCreateDraftInternalLayerVersionMutation';

import { LayerFeaturesContent } from './LayerFeaturesContent';
import { RequestApprovalModal } from './RequestApprovalModal';
import { useDeleteDraftInternalLayerVersionMutation } from 'hooks/internalLayerVersion/useDeleteDraftInternalLayerVersionMutation';
import { useDeleteLocation } from 'hooks/icsLocation/useDeleteLocation';

export function CreateEditInternalLayerVersionContent({
  internalLayer,
  showCreateDraftButton,
  ...props
}: {
  internalLayer: InternalLayer;
  onBack: () => void;
  showCreateDraftButton?: boolean;
}) {
  const {
    stopDrawing,
    getSelectedVersionForLayer,
    sidebarSelectedInternalLayer,
  } = useUscgMapContext();

  const createDraft = useCreateDraftInternalLayerVersionMutation();
  const deleteDraft = useDeleteDraftInternalLayerVersionMutation();
  const deleteLocation = useDeleteLocation();

  const selectedInternalLayerVersion = sidebarSelectedInternalLayer?.id
    ? getSelectedVersionForLayer(sidebarSelectedInternalLayer.id)
    : undefined;

  const onBack = () => {
    stopDrawing();
    props.onBack();
  };

  const onCreate = async () => {
    await createDraft.mutateAsync({
      internalLayerId: internalLayer.id,
    });
    onBack();
  };

  const onDelete = async () => {
    if (!selectedInternalLayerVersion?.id) {
      return;
    }

    await deleteDraft.mutateAsync({
      internalLayerVersionId: selectedInternalLayerVersion?.id,
    });
    onBack();
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        {showCreateDraftButton ? (
          <StylishNewButton
            className="button button--secondary"
            disabled={createDraft.isPending}
            onClick={onCreate}
          >
            Create draft
          </StylishNewButton>
        ) : (
          <>
            <RequestApprovalModal
              version={selectedInternalLayerVersion}
              onBack={onBack}
            />
            <StylishNewButton
              className="button button--secondary"
              onClick={onDelete}
            >
              Delete
            </StylishNewButton>
          </>
        )}

        <StylishNewButton className="button button--secondary" onClick={onBack}>
          Cancel
        </StylishNewButton>
      </div>
      <LayerMetadata
        lastUpdated={formatDateTime(internalLayer.updatedAt)}
        aorIds={internalLayer.aorIds}
        incident={internalLayer.incidentId}
        sources="PRATUS"
      />

      <div className="mt-3">
        <LayerFeaturesContent
          layer={internalLayer}
          layerType="internal"
          showAddObjectButton
          onDelete={async (objectId) => {
            await deleteLocation.mutateAsync(objectId);
          }}
        />
      </div>
    </>
  );
}
